import {
  ComponentType,
  forwardRef,
  ReactElement,
  ReactNode,
  useEffect,
  useState
} from 'react';
import { Link } from 'react-router-dom';
import { Box } from '../Box';
import { DefaultListComponent } from './components/DefaultListComponent';

import styles from './index.module.css';

interface TabItem {
  component?: ReactNode;
  counter?: string | number;
  label: string;
  onActive?: (index: number) => void;
  disabled?: boolean;
}

interface ClassNames {
  tabsClassName?: string;
  tabClassName?: string;
  componentClassName?: string;
  counterClassName?: string;
  tabActiveClassName?: string;
  tabLinkClassName?: string;
}

interface TabsProps {
  list?: TabItem[];
  activeTabIndex?: number;
  initialTabIndex?: number;
  isDesktop?: boolean;
  classNames?: ClassNames;
  ListComponent?: ComponentType<{
    counterClassName?: string;
    handleClick: (index: number) => void;
    list: TabItem[];
    tabClassName?: string;
    tabActiveClassName?: string;
    tabIndex: number;
    isDesktop: boolean;
    LocalizedNavLink: (data: { to: string }) => ReactElement;
    pathname: string;
  }>;
  LocalizedNavLink?: (data: { to: string }) => ReactElement;
  pathname?: string;
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      list = [],
      activeTabIndex = 0,
      initialTabIndex = 0,
      isDesktop = false,
      classNames = {},
      ListComponent = DefaultListComponent,
      LocalizedNavLink = (data) => <Link {...data} />,
      pathname = '/'
    },
    ref
  ) => {
    const filteredList = list.filter(({ disabled }) => !disabled);
    const [tabIndex, setTabIndex] = useState(initialTabIndex);
    const { component } = filteredList[tabIndex] || {};

    const {
      tabsClassName = '',
      tabClassName = '',
      tabLinkClassName = '',
      componentClassName = '',
      counterClassName = '',
      tabActiveClassName = ''
    } = classNames;

    useEffect(() => {
      if (activeTabIndex !== null && tabIndex !== activeTabIndex) {
        setTabIndex(activeTabIndex);
      }
    }, [activeTabIndex]);

    const handleClick = (index: number) => {
      const { onActive = () => {} } = filteredList[index];
      onActive(index);
      setTabIndex(index);
    };

    return (
      <>
        <Box
          align="end"
          flexShrink={0}
          className={`${styles.tabs} ${tabsClassName}`}
        >
          <ListComponent
            counterClassName={counterClassName}
            handleClick={handleClick}
            list={filteredList}
            tabClassName={tabClassName}
            tabLinkClassName={tabLinkClassName}
            tabActiveClassName={tabActiveClassName}
            tabIndex={tabIndex}
            isDesktop={isDesktop}
            LocalizedNavLink={LocalizedNavLink}
            pathname={pathname}
          />
        </Box>
        {component && (
          <div className={componentClassName} ref={ref}>
            {component}
          </div>
        )}
      </>
    );
  }
);
